import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from "@tanstack/react-query";
import { apiFetch } from "@/lib/api";
import { useAuth } from "@/modules/auth/hooks/useAuth";
import { useCategoryItems } from "@/modules/dropdowns/hooks/useDropdowns";
import type {
  ConvertPayload,
  ConvertResult,
  CreateEnquiryPayload,
  EnquiryDetail,
  EnquiryListResponse,
  UpdateEnquiryPayload,
} from "../types";

const KEY = ["enquiries"] as const;

export type EnquiryListParams = {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
  priority?: string;
  category?: string;
  assignedToId?: string;
  linkedContactId?: string;
  leadId?: string;
  source?: string[];
  dateFrom?: string;
  dateTo?: string;
  tab?: "all" | "new" | "mine" | "closed" | "spam";
};

export function useEnquiryList(
  params: EnquiryListParams = {},
): UseQueryResult<EnquiryListResponse> {
  const { accessToken } = useAuth();
  return useQuery({
    queryKey: [...KEY, "list", params, accessToken ? "auth" : "anon"],
    enabled: !!accessToken,
    queryFn: () =>
      apiFetch<EnquiryListResponse>("/enquiries", {
        token: accessToken,
        query: {
          page: params.page,
          limit: params.limit,
          search: params.search,
          status: params.status,
          priority: params.priority,
          category: params.category,
          assignedToId: params.assignedToId,
          linkedContactId: params.linkedContactId,
          leadId: params.leadId,
          source: params.source,
          dateFrom: params.dateFrom,
          dateTo: params.dateTo,
          tab: params.tab,
        },
      }),
  });
}

export function useEnquiry(id: string): UseQueryResult<EnquiryDetail> {
  const { accessToken } = useAuth();
  return useQuery({
    queryKey: [...KEY, "detail", id, accessToken ? "auth" : "anon"],
    enabled: !!accessToken && !!id,
    queryFn: async () => {
      const res = await apiFetch<{ enquiry: EnquiryDetail }>(
        `/enquiries/${id}`,
        { token: accessToken },
      );
      return res.enquiry;
    },
  });
}

export function useCreateEnquiry() {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateEnquiryPayload) =>
      apiFetch<{ enquiry: EnquiryDetail }>("/enquiries", {
        method: "POST",
        body: payload,
        token: accessToken,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useUpdateEnquiry(id: string) {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: UpdateEnquiryPayload) =>
      apiFetch<{ enquiry: EnquiryDetail }>(`/enquiries/${id}`, {
        method: "PATCH",
        body: payload,
        token: accessToken,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useAssignEnquiry(id: string) {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (assignedToId: string | null) =>
      apiFetch<{ enquiry: EnquiryDetail }>(`/enquiries/${id}/assign`, {
        method: "POST",
        body: { assignedToId },
        token: accessToken,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useMarkSpam(id: string) {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { isSpam: boolean; reason?: string }) =>
      apiFetch<{ enquiry: EnquiryDetail }>(`/enquiries/${id}/mark-spam`, {
        method: "POST",
        body: vars,
        token: accessToken,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useDeleteEnquiry() {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch(`/enquiries/${id}`, { method: "DELETE", token: accessToken }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useConvertEnquiry(id: string) {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: ConvertPayload) =>
      apiFetch<ConvertResult>(`/enquiries/${id}/convert`, {
        method: "POST",
        body: payload,
        token: accessToken,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY });
      qc.invalidateQueries({ queryKey: ["contacts"] });
    },
  });
}

export const useEnquiryStatuses = () => useCategoryItems("enquiry_status");
export const useEnquiryPriorities = () => useCategoryItems("enquiry_priority");
export const useEnquiryCategories = () => useCategoryItems("enquiry_category");
export const useEnquirySources = () => useCategoryItems("enquiry_source");
